import React, { useState, useEffect } from 'react';
import { BookOpen, CheckCircle, Clock, Award, Plus, X, Loader2, Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import ConfirmDialog from '../shared/ConfirmDialog';

const trainingCategories = ['Infection Control', 'Patient Safety', 'Compliance', 'Emergency', 'Clinical', 'Administrative'];
const emptyForm = { title: '', description: '', category: '', department: '', duration_minutes: 30, due_date: '', content_url: '', status: 'active' };

const TrainingPage = () => {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadTrainings = () => { setLoading(true); api.get('/api/training').then(setTrainings).catch(e => toast.error(e.message)).finally(() => setLoading(false)); };
  useEffect(() => { loadTrainings(); }, []);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setShowModal(true); };
  const openEdit = (t) => {
    setForm({ title: t.title || '', description: t.description || '', category: t.category || '', department: t.department || '', duration_minutes: t.duration_minutes || 30, due_date: t.due_date || '', content_url: t.content_url || '', status: t.status || 'active' });
    setEditingId(t.id); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.description || !form.category || !form.due_date) { toast.error('Fill required fields'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, duration_minutes: parseInt(form.duration_minutes) };
      if (editingId) { await api.put(`/api/training/${editingId}`, payload); toast.success('Training updated'); }
      else { await api.post('/api/training', payload); toast.success('Training created'); }
      setShowModal(false); setForm(emptyForm); setEditingId(null); loadTrainings();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleting(true);
    try { await api.delete(`/api/training/${deleteTarget.id}`); toast.success('Training deleted'); setDeleteTarget(null); loadTrainings(); }
    catch (e) { toast.error(e.message); } finally { setDeleting(false); }
  };

  const filteredTrainings = trainings.filter(t => filter === 'all' || t.status === filter);

  return (
    <div className="space-y-6" data-testid="training-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-gray-900">Education & Training</h1><p className="text-gray-500 text-sm mt-1">Manage training modules and track completion</p></div>
        <Button onClick={openCreate} className="btn-primary gap-2" data-testid="create-training-btn"><Plus className="w-4 h-4" />Create Module</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100"><BookOpen className="w-4 h-4 text-[var(--niko-teal)] mb-2" /><p className="text-2xl font-bold text-gray-900" data-testid="total-trainings">{trainings.length}</p><p className="text-sm text-gray-500">Total</p></div>
        <div className="bg-white rounded-xl p-4 border border-gray-100"><CheckCircle className="w-4 h-4 text-green-500 mb-2" /><p className="text-2xl font-bold text-gray-900">{trainings.filter(t => t.status === 'active').length}</p><p className="text-sm text-gray-500">Active</p></div>
        <div className="bg-white rounded-xl p-4 border border-gray-100"><Clock className="w-4 h-4 text-yellow-500 mb-2" /><p className="text-2xl font-bold text-gray-900">{trainings.reduce((a, t) => a + (t.completions || 0), 0)}</p><p className="text-sm text-gray-500">Completions</p></div>
        <div className="bg-white rounded-xl p-4 border border-gray-100"><Award className="w-4 h-4 text-purple-500 mb-2" /><p className="text-2xl font-bold text-gray-900">{new Set(trainings.map(t => t.category)).size}</p><p className="text-sm text-gray-500">Categories</p></div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {['all', 'active'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f ? 'bg-[var(--niko-teal)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div> : (
        <div className="space-y-4">
          {filteredTrainings.length === 0 ? <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-400">{trainings.length === 0 ? 'No modules yet.' : 'No match'}</div> :
          filteredTrainings.map(training => (
            <div key={training.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow group" data-testid="training-item">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[var(--niko-teal)]/10"><BookOpen className="w-6 h-6 text-[var(--niko-teal)]" /></div>
                  <div>
                    <h3 className="font-medium text-gray-900">{training.title}</h3>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1"><span>{training.category}</span><span>-</span><span>{training.duration_minutes} min</span>{training.due_date && <><span>-</span><span>Due: {training.due_date}</span></>}</div>
                    {training.description && <p className="text-sm text-gray-500 mt-1 line-clamp-1">{training.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{training.completions || 0} completions</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${training.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{training.status}</span>
                  </div>
                  <button onClick={() => openEdit(training)} className="p-2 text-gray-400 hover:text-[var(--niko-teal)] hover:bg-[var(--niko-teal)]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all" data-testid="edit-training-btn"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => setDeleteTarget(training)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all" data-testid="delete-training-btn"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="training-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingId ? 'Edit Training' : 'Create Module'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><Label>Title *</Label><Input className="mt-1" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="training-title-input" /></div>
              <div><Label>Description *</Label><Textarea className="mt-1" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div><Label>Category *</Label><select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.category} onChange={e => setForm({...form, category: e.target.value})}><option value="">Select...</option>{trainingCategories.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Duration (min)</Label><Input type="number" className="mt-1" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: e.target.value})} /></div>
                <div><Label>Due Date *</Label><Input type="date" className="mt-1" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
              </div>
              <div><Label>Department</Label><Input className="mt-1" value={form.department} onChange={e => setForm({...form, department: e.target.value})} /></div>
              <div><Label>Content URL</Label><Input className="mt-1" value={form.content_url} onChange={e => setForm({...form, content_url: e.target.value})} /></div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-training-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingId ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleting} title="Delete Training" message={`Delete "${deleteTarget?.title}"? This cannot be undone.`} />
    </div>
  );
};

export default TrainingPage;
